import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewEncapsulation } from '@angular/core'
import { PagedResult } from '@ftr/contracts/api/core/response/PagedResult'
import { PageMetadata } from '@ftr/contracts/type/shared'
import {
  AnimatedRemoteComponent,
  ApiResult,
  DestroySubscribers,
  ErrorRetryComponent,
  Icon,
  IconComponent,
  LineLoaderComponent,
  NotificationModule,
  NotificationType,
  fade,
  tapData,
} from '@ftr/foundation'
import { takeUntil } from 'rxjs'
import { PaginationComponent } from '../pagination'

@Component({
  selector: 'ftr-list-layout',
  templateUrl: './list-layout.component.html',
  styleUrls: ['./list-layout.component.css'],
  encapsulation: ViewEncapsulation.None,
  animations: [fade(500, 0)],
  standalone: true,
  imports: [
    AnimatedRemoteComponent,
    CommonModule,
    ErrorRetryComponent,
    IconComponent,
    LineLoaderComponent,
    NotificationModule,
    PaginationComponent,
  ],
})
/**
 * A component for encapsulating display of a paginated list of cards.
 * Includes the action header containing the title and a paginator.
 */
export class ListLayoutComponent<T> extends DestroySubscribers implements OnInit {
  /**
   * How to refer to more than one item in the list
   */
  @Input() plural: string
  /**
   * An optional icon for the entity type that makes up this list
   */
  @Input() icon?: Icon
  /**
   * Should this list occupy the full width of its container
   */
  @Input() fullWidth = false
  /**
   * The items in the list
   */
  @Input() entities: ApiResult<PagedResult<T>>
  /**
   * An optional success notification to display
   */
  @Input() successMessage?: string
  /**
   * An optional error notification to display
   */
  @Input() errorMessage?: string
  /**
   * A template for the list
   */
  @Input() listTemplate: TemplateRef<unknown> | null = null
  /**
   * A template for the list's header. This will be displayed above the list's content
   * in the empty, error, and success state.
   */
  @Input() listHeaderTemplate: TemplateRef<unknown> | null = null
  /**
   * A template for the header
   */
  @Input() headerTemplate: TemplateRef<unknown> | null = null
  /**
   * Optional template for an empty list
   */
  @Input() emptyListTemplate: TemplateRef<unknown> | null = null
  /**
   * Optional loading template
   */
  @Input() loadingTemplate: TemplateRef<unknown> | null = null
  /**
   * Optional error template
   */
  @Input() errorTemplate: TemplateRef<unknown> | null = null
  /**
   * Optional template for a filter which sits along side pagination
   */
  @Input() filterTemplate: TemplateRef<unknown> | null = null
  /**
   * An optional setting that suppresses standard top paginator
   * allowing the calling component to handle it
   */
  @Input() hideTopPaginator?: boolean
  /**
   * An optional setting that suppresses standard bottom paginator
   * allowing the calling component to handle it
   */
  @Input() hideBottomPaginator?: boolean
  /**
   * An optional setting that suppresses notifications in this component
   */
  @Input() hideNotifications?: boolean
  /**
   * The class name to apply to the root element.
   * If the class name is not provided, it defaults to plural.
   * A separate class name is required in case of a variable plural value,
   * e.g. the one that comes from vocabulary terms.
   */
  @Input() className?: string
  /**
   * Used to emit that the user has requested that the data should be loaded again
   */
  @Output() onRetry = new EventEmitter<undefined>()
  /**
   * Label to use for empty text if you are externally filtering the entities list.
   *
   * Eg 'You have no Active Courtrooms yet.'
   */
  @Input() currentFilter?: string
  /**
   * By default, we use `card media` classes on empty and error states.
   * Set useCards to `false` to remove these classes and apply your own styles.
   */
  @Input() useCards: boolean = true
  /**
   * The class name to apply to the root element
   */
  class: string

  /**
   * Page metadata for pagination
   */
  meta?: PageMetadata

  readonly notificationType = NotificationType

  ngOnInit(): void {
    this.setClass(this.className || this.plural)
    this.entities
      .pipe(
        takeUntil(this.finalize),
        tapData(data => (this.meta = data.meta)),
      )
      .subscribe()
  }

  retry(): void {
    this.onRetry.emit()
  }

  isEmpty(data: PagedResult<T>): boolean {
    return data.items.length === 0
  }

  private setClass(name: string): void {
    this.class = name.toLowerCase().replace(' ', '-')
  }
}
